import React, { useState, useEffect } from 'react';
import { Code, Award, Layers, Gamepad2, Sparkles } from 'lucide-react';

const roles = [
  'Desarrollador de Videojuegos',
  'Especialista en Unity',
  'Creador de Herramientas',
  'Arquitecto de Editores',
];

const techTags = [
  'Unity', 'C#', 'VR', 'AR', 'Firebase', 'PlayFab',
  'C++', '.NET', 'Veldrid', 'DirectX 12', 'Vulkan', 'Python', 'IA', 'DeepSeek',
];

export default function Hero({ visible }) {
  const [roleIndex, setRoleIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!visible) return;

    const currentRole = roles[roleIndex];
    let timeout;

    if (!isDeleting) {
      if (displayText.length < currentRole.length) {
        timeout = setTimeout(() => {
          setDisplayText(currentRole.slice(0, displayText.length + 1));
        }, 60);
      } else {
        timeout = setTimeout(() => setIsDeleting(true), 2000);
      }
    } else {
      if (displayText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 30);
      } else {
        setIsDeleting(false);
        setRoleIndex((prev) => (prev + 1) % roles.length);
      }
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, roleIndex, visible]);

  return (
    <section className="relative min-h-screen flex items-center justify-center px-6 lg:px-12 pt-24 pb-20">
      {/* Gradient orbs */}
      <div className="orb w-[500px] h-[500px] bg-accent-blue/10 top-[-10%] left-[-10%]" />
      <div className="orb w-[400px] h-[400px] bg-accent-purple/8 bottom-[-5%] right-[-5%]" />
      <div className="orb w-[300px] h-[300px] bg-accent-cyan/6 top-[40%] right-[20%]" />

      {/* Floating particles (decorative) */}
      {visible && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                width: `${2 + Math.random() * 4}px`,
                height: `${2 + Math.random() * 4}px`,
                background: `radial-gradient(circle, ${
                  ['rgba(59,130,246,0.6)', 'rgba(139,92,246,0.6)', 'rgba(34,211,238,0.6)'][i % 3]
                }, transparent)`,
                left: `${Math.random() * 100}%`,
                bottom: `${Math.random() * 20}%`,
                animationDuration: `${15 + Math.random() * 20}s`,
                animationDelay: `${Math.random() * 10}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto text-center">
        {/* Badge */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass glass-hover mb-8 lg:mb-10 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '0.1s' }}
        >
          <Sparkles className="w-3.5 h-3.5 text-accent-cyan" />
          <span className="text-xs font-medium text-gray-300 tracking-wide">
            Disponible para proyectos
          </span>
        </div>

        {/* Main Title */}
        <h1
          className={`font-display font-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl leading-[0.9] tracking-tight mb-6 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '0.2s' }}
        >
          <span className="block text-white">Euclides</span>
          <span className="text-gradient">Martinez</span>
        </h1>

        {/* Typewriter role */}
        <div
          className={`h-8 lg:h-10 flex items-center justify-center mb-8 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '0.35s' }}
        >
          <span className="text-lg lg:text-xl text-gray-400 font-light tracking-wide">
            <span className="text-accent-cyan">&lt;</span>
            <span className="text-white/80 typewriter-cursor">{displayText}</span>
            <span className="text-accent-cyan"> /&gt;</span>
          </span>
        </div>

        {/* Description */}
        <p
          className={`max-w-2xl mx-auto text-sm lg:text-base text-gray-500 leading-relaxed mb-10 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '0.5s' }}
        >
          Especializado en{' '}
          <span className="text-accent-blue font-medium">Unity</span> con enfoque
          en{' '}
          <span className="text-accent-purple font-medium">
            creación de editores y automatizaciones
          </span>.
          Transformo workflows complejos en herramientas intuitivas que potencian
          la productividad del equipo.
        </p>

        {/* Stats grid */}
        <div
          className={`grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-10 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '0.6s' }}
        >
          {[
            { icon: Code, label: 'Unity & C#', color: 'text-accent-blue' },
            { icon: Award, label: 'Unity Editor', color: 'text-accent-purple' },
            { icon: Layers, label: 'VR & AR', color: 'text-accent-cyan' },
            { icon: Gamepad2, label: '40+ Proyectos', color: 'text-accent-blue' },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-center justify-center gap-2.5 px-3 py-3 rounded-xl glass glass-hover group"
            >
              <item.icon className={`w-4 h-4 ${item.color} transition-transform duration-300 group-hover:scale-110`} />
              <span className="text-xs text-gray-400 font-medium">{item.label}</span>
            </div>
          ))}
        </div>

        {/* Tech tags */}
        <div
          className={`flex flex-wrap justify-center gap-2 max-w-3xl mx-auto transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '0.75s' }}
        >
          {techTags.map((tech, i) => (
            <span
              key={i}
              className="px-3 py-1.5 text-xs rounded-lg bg-white/[0.03] border border-white/[0.06] text-gray-500 hover:text-gray-300 hover:border-accent-blue/20 hover:bg-accent-blue/[0.03] transition-all duration-300 cursor-default"
            >
              {tech}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-700 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDelay: '1s' }}
      >
        <div className="flex flex-col items-center gap-2 text-gray-600">
          <span className="text-[10px] uppercase tracking-[0.3em] font-mono">Scroll</span>
          <div className="w-4 h-7 rounded-full border border-gray-700 flex justify-center pt-1.5">
            <div className="w-1 h-1.5 rounded-full bg-accent-blue/60 animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
}
